'use client';
import React, { useState } from 'react';

const plans = [
  {
    id: 'audio-15',
    type: 'Audio',
    duration: '30 min',
    price: 18.95,
    currency: '£',
    description: 'Idéal pour une conversation rapide ou un sujet précis',
    icon: '🎧',
    badge: null,
  },
  {
    id: 'video-15',
    type: 'Vidéo Face to Face',
    duration: '30 min',
    price: 17.50,
    currency: '£',
    description: 'Face à face virtuel pour plus d\'expressivité',
    icon: '📹',
    badge: null,
  },
  {
    id: 'audio-45',
    type: 'Audio',
    duration: '1 heure',
    price: 35,
    currency: '£',
    description: 'Session complète : conversation, correction, progression',
    icon: '🎧',
    badge: 'Populaire',
  },
  {
    id: 'video-45',
    type: 'Vidéo',
    duration: '1 heure',
    price: 40,
    currency: '£',
    description: 'La session immersion complète avec Marc',
    icon: '📹',
    badge: 'Recommandé',
  },
  {
    id: 'city-immersion',
    type: 'Rendez-vous en ville',
    duration: '1 heure',
    price: 45,
    currency: '£',
    description: 'Total Immersion — cours live en ville avec Marc pour une expérience authentique',
    icon: '🏙️',
    badge: 'Nouveau',
  },
];

interface PricingCardsProps {
  onSelect?: (planId: string) => void;
  selected?: string;
}

export default function PricingCards({ onSelect, selected }: PricingCardsProps) {
  const [localSelected, setLocalSelected] = useState<string>('audio-45');
  const activeId = selected ?? localSelected;

  const handleSelect = (id: string) => {
    setLocalSelected(id);
    onSelect?.(id);
    // Scroll to form
    const form = document.getElementById('booking-form');
    if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="py-16 lg:py-20 bg-background">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-primary mb-2">
            Choisissez votre formule
          </h2>
          <p className="text-secondary text-sm">Sessions en ligne via Zoom ou Google Meet · Rendez-vous en ville disponible</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 lg:gap-6">
          {plans.map((plan) => (
            <div
              key={`plan-${plan.id}`}
              onClick={() => handleSelect(plan.id)}
              className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all duration-200 ${
                activeId === plan.id
                  ? 'border-accent bg-amber-50/60 shadow-amber scale-[1.02]'
                  : 'border-border bg-white hover:border-accent/50 hover:shadow-card'
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent rounded-full text-xs font-bold text-primary shadow-sm">
                  {plan.badge}
                </div>
              )}

              {/* Selected indicator */}
              {activeId === plan.id && (
                <div className="absolute top-3 right-3 w-5 h-5 bg-accent rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}

              <div className="text-3xl mb-3">{plan.icon}</div>

              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-extrabold text-primary font-tabular">{plan.currency}{plan.price}</span>
              </div>

              <p className="text-sm font-semibold text-accent mb-1">
                {plan.type} · {plan.duration}
              </p>

              <p className="text-sm text-secondary leading-snug mb-5">{plan.description}</p>

              <button
                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 ${
                  activeId === plan.id
                    ? 'btn-primary' :'border border-accent text-accent hover:bg-accent hover:text-primary'
                }`}
              >
                Réserver cette séance
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}